import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core'

import { Subject } from 'rxjs'

import { DragAnimation, DragEndParams, DragMoveParams, DragType, Position } from './drag.models'
import { DragService } from './drag.service'
import { FreeMoveService } from './free-move.service'
import { MagneticMoveService } from './magnetic-move.service'

@Directive({
  selector: '[flxDrag]',
  standalone: true,
})
export class DragDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input() flxDrag: DragAnimation = DragType.FREE
  @Input() flxDragPadding: Position | null = null
  @Input() flxDragTarget: HTMLElement | null = null

  @Output() flxDragInit? = new EventEmitter()
  @Output() flxDragEnd = new EventEmitter()
  @Output() flxDragMove = new EventEmitter()

  dragInstance: Subject<void>
  dragHandler: HTMLElement
  dragElement: HTMLElement
  onDragMove: (params: DragMoveParams) => Position
  onDragEnd: (params: DragEndParams) => Position

  dragTypeService: FreeMoveService | MagneticMoveService
  dragTypeServicesMap: Record<DragType, typeof this.dragTypeService>
  resizeListener

  constructor(
    private el: ElementRef,
    private dragService: DragService,
    private freeMoveService: FreeMoveService,
    private magneticMoveService: MagneticMoveService
  ) {
    this.dragHandler = this.el.nativeElement
    this.dragElement = this.flxDragTarget ?? this.el.nativeElement

    this.dragTypeServicesMap = {
      [DragType.FREE]: this.freeMoveService,
      [DragType.MAGNETIC]: this.magneticMoveService,
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.flxDragTarget) {
      this.dragElement = this.flxDragTarget ?? this.el.nativeElement
    }

    if (changes.flxDrag) {
      this.dragTypeService =
        this.dragTypeServicesMap[this.flxDrag] ?? this.dragTypeServicesMap[DragType.FREE]

      this.onDragMove = (args) => this.dragTypeService.onDragMove(args)
      this.onDragEnd = (args) => this.dragTypeService.onDragEnd(args)

      if (!this.resizeListener) {
        window.addEventListener('resize', this.onResize.bind(this))
      }
    }
  }

  ngAfterViewInit(): void {
    this.dragInstance = this.dragService.init(this.dragHandler, {
      onDragEnd: (data) => {
        if (this.onDragMove) {
          const newPosition = this.onDragEnd({
            element: this.dragElement,
            toPosition:
              this.dragHandler !== this.dragElement
                ? this.handlerToDragPosition({
                    handlerEl: this.dragHandler,
                    dragEl: this.dragElement,
                    toPosition: data.to,
                  })
                : data.to,
            padding: this.flxDragPadding ?? undefined,
          })

          this.flxDragEnd.emit({ to: newPosition, delta: data.delta, manual: true })
        }
      },
      onDragMove: (data) => {
        if (this.onDragEnd) {
          const newPosition = this.onDragMove({
            element: this.dragElement,
            toPosition:
              this.dragHandler !== this.dragElement
                ? this.handlerToDragPosition({
                    handlerEl: this.dragHandler,
                    dragEl: this.dragElement,
                    toPosition: data.to,
                  })
                : data.to,
          })

          this.flxDragMove.emit({ to: newPosition, delta: data.deltaAbs })
        }
      },
    })

    // if (this.flxDragInit && this.dragTypeService) {
    //   const newPosition = this.dragTypeService.calculateElementNewPosition({
    //     element: this.dragElement,
    //     toPosition: this.dragElement.getBoundingClientRect(),
    //   })

    //   this.flxDragInit.emit({
    //     to: newPosition,
    //   })
    // }
  }

  ngOnDestroy(): void {
    this.dragInstance.next()
    this.dragInstance.complete()
    window.removeEventListener('resize', this.onResize.bind(this))
  }

  private handlerToDragPosition({
    handlerEl,
    dragEl,
    toPosition,
  }: {
    handlerEl: HTMLElement
    dragEl: HTMLElement
    toPosition: { x: number; y: number }
  }): { x: number; y: number } {
    const { left: handlerLeft, top: handlerTop } = handlerEl.getBoundingClientRect()
    const { left: dragLeft, top: dragTop } = dragEl.getBoundingClientRect()

    return {
      x: toPosition.x + (dragLeft - handlerLeft),
      y: toPosition.y + (dragTop - handlerTop),
    }
  }

  private onResize(): void {
    const newPosition = this.dragTypeService.calculateElementNewPosition({
      element: this.dragElement,
      toPosition: this.dragElement.getBoundingClientRect(),
    })

    this.flxDragEnd.emit({ to: newPosition })
  }
}
